import { useState, type SubmitEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { createCategory, updateCategory, type ProductCategory } from '@/api/productCatalog'
import { useApiErrorMessage } from '@/api/errors'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

export function CategoryDialog({
  open,
  category,
  onOpenChange,
  onSaved,
}: {
  open: boolean
  /** Null creates a category; one edits it. */
  category: ProductCategory | null
  onOpenChange: (open: boolean) => void
  onSaved: () => void
}) {
  const { t } = useTranslation()

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {category ? t('catalog.category.editTitle') : t('catalog.category.createTitle')}
          </DialogTitle>
          <DialogDescription>
            {category ? t('catalog.category.editDescription') : t('catalog.category.createDescription')}
          </DialogDescription>
        </DialogHeader>
        {open && (
          <CategoryForm
            key={category?.id ?? 'new'}
            category={category}
            onOpenChange={onOpenChange}
            onSaved={onSaved}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}

function CategoryForm({
  category,
  onOpenChange,
  onSaved,
}: {
  category: ProductCategory | null
  onOpenChange: (open: boolean) => void
  onSaved: () => void
}) {
  const { t } = useTranslation()
  const errorMessage = useApiErrorMessage()

  const [name, setName] = useState(category?.name ?? '')
  const [active, setActive] = useState(category?.active ?? true)
  const [error, setError] = useState<string | null>(null)
  const [failure, setFailure] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  async function submit(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault()
    if (submitting) {
      return
    }
    if (!name.trim()) {
      setError(t('validation.required'))
      return
    }
    setError(null)
    setSubmitting(true)
    setFailure(null)
    // sortIndex is left out on purpose: omitting it appends a new category and leaves an existing
    // one where it is. Reordering is its own job, not a side effect of a rename.
    const body = { name: name.trim(), active }
    try {
      if (category) {
        await updateCategory(category.id, body)
        toast.success(t('catalog.category.updated', { name: body.name }))
      } else {
        await createCategory(body)
        toast.success(t('catalog.category.created', { name: body.name }))
      }
      onSaved()
      onOpenChange(false)
    } catch (caught) {
      setFailure(errorMessage(caught, t('catalog.category.failed')))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form onSubmit={submit} noValidate>
      <div className="grid gap-4 py-2">
        {failure && (
          <Alert variant="destructive">
            <AlertDescription>{failure}</AlertDescription>
          </Alert>
        )}

        <div className="grid gap-2">
          <Label htmlFor="category-name">{t('catalog.category.name')}</Label>
          <Input
            id="category-name"
            value={name}
            aria-invalid={Boolean(error)}
            aria-describedby={error ? 'category-name-error' : 'category-name-hint'}
            onChange={(event) => setName(event.target.value)}
          />
          {error ? (
            <p id="category-name-error" className="text-destructive text-xs">
              {error}
            </p>
          ) : (
            <p id="category-name-hint" className="text-muted-foreground text-xs">
              {t('catalog.renameHint')}
            </p>
          )}
        </div>

        <div className="flex items-start gap-2">
          <Checkbox
            id="category-active"
            checked={active}
            onCheckedChange={(checked) => setActive(checked === true)}
          />
          <div className="grid gap-1">
            <Label htmlFor="category-active" className="font-normal">
              {t('catalog.activeLabel')}
            </Label>
            <p className="text-muted-foreground text-xs">{t('catalog.activeHint')}</p>
          </div>
        </div>
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" disabled={submitting}>
          {category ? t('catalog.save') : t('catalog.create')}
        </Button>
      </DialogFooter>
    </form>
  )
}
